/**
 * Permission constants for the application
 *
 * These constants represent permission names that come from the API response
 * and are stored in IndexDB. Using constants instead of hardcoded strings
 * provides:
 * - Type safety
 * - Centralized management
 * - Easy refactoring
 * - Better IDE autocomplete
 * - Reduced typo errors
 *
 * Note: These values must match the permission names returned from the API.
 */
export const PERMISSIONS = {
    // Lead Management Permissions
    VIEW_MY_LEAD: 'view_my_lead',
    VIEW_TEAM_LEADS: 'view_team_leads',
    PROSPECT_LEAD: 'prospect_lead',

    // RUC Dashboard Permissions
    VIEW_MY_RUC_DASHBOARD: 'view_my_ruc_dashboard',
    VIEW_TEAM_RUC_DASHBOARD: 'view_team_ruc_dashboard',

    // Revenue dashboard permissions
    VIEW_MY_REVENUE_DASHBOARD: 'view_my_revenue_dashboard',
    VIEW_TEAM_REVENUE_DASHBOARD: 'view_team_revenue_dashboard',

    // Sales Assist Permissions
    ENABLE_SALES_ASSIST: 'enable_sales_assist',

    // Client Management Permissions
    VIEW_CLIENT: 'view_client',
    ADD_NEW_CLIENT: 'add_new_client',
    APPROVE_CLIENT: 'approve_client',
    CREATE_CLIENT_SHIKSHA: 'create_shiksha_client',
    VIEW_CLIENT_SHIKSHA: 'view_shiksha_client',

    // Deal Management Permissions
    VIEW_DEAL_SHIKSHA: 'view_deal_shiksha',
    CREATE_DEAL_SHIKSHA: 'create_deal_shiksha',

    // Proposal/Order Permissions
    APPROVE_PROPOSAL: 'approve_proposal',
    VIEW_ORDER: 'view_order',
    VIEW_RETENTION_CHECKS: 'view_retention_checks',

    // School Site Permissions
    VIEW_SCHOOL_SITE: 'view_school_site',
    MANAGE_STUDENT: 'manage_student',
    MANAGE_TEACHER: 'manage_teacher',
    MANAGE_ADMIN: 'manage_admin',
    MANAGE_SCHOOL_SITE_SETTINGS: 'manage_school_site_settings',
    MANAGE_SCHOOL_SITE_DUAL_LOGIN: 'manage_school_site_dual_login',

    // Student Management Permissions
    ADD_EDIT_NEW_STUDENT: 'add_edit_new_student',

    // Dashboard Permissions
    MY_MATRIX: 'my_matrix',
    MY_MATRIX_VIEW: 'my_matrix_view',
    MY_DEALS: 'my_deals',
    MY_FUNNEL: 'my_funnel',

    //Service Desk Permissions
    VIEW_SERVICE_DESK: 'view_service_desk',

    // Finance payment reconciliation (NAV-12636: view the queue and claims; review = lock, approve, reject)
    VIEW_PAYMENT_CLAIMS: 'view_payment_claims',
    REVIEW_PAYMENT_CLAIMS: 'review_payment_claims',

    // School Intelligence System (SIS) Permissions
    VIEW_SIS_PORTFOLIO: 'sis_portfolio_view',

    // Academic Calendar Permissions
    VIEW_ACADEMIC_CALENDAR: 'view_academic_calendar',

    // Field Visit Permissions
    VIEW_FIELD_VISIT: 'view_field_visit',

    // Translation Management Permissions
    VIEW_TRANSLATION_MANAGEMENT: 'view_translation_management',

    // MCP Dashboard Permissions
    VIEW_MCP_DASHBOARD: 'view_mcp_dashboard',

    // Settings Permissions
    VIEW_SETTINGS: 'view_settings',
    CREATE_MCP_TOKEN: 'create_mcp_token',

    // Blog Management Permissions
    MANAGE_BLOGS: 'manage_blogs',

    // Website content (Ei Study) — teacher resources, CBE, teams
    MANAGE_TEACHER_RESOURCES: 'manage_teacher_resources',
    MANAGE_CBE_RESOURCES: 'manage_cbe_resources',
    MANAGE_TEAMS: 'manage_teams',
    MANAGE_CAREERS: 'manage_careers',
    MANAGE_NEWS: 'manage_news',
    MANAGE_WEBINARS: 'manage_webinars',
    MANAGE_DOWNLOADS: 'manage_downloads',
    MANAGE_NEWSLETTERS: 'manage_newsletters',

    // EI Banner campaign admin
    MANAGE_EI_BANNER: 'manage_ei_banner',

    // Product-specific Translation Permissions
    VIEW_ASSET_TRANSLATION: 'view_asset_translation',
    VIEW_CARES_TRANSLATION: 'view_cares_translation',
    VIEW_MIRA_TRANSLATION: 'view_mira_translation',
    VIEW_MINDSPARK_TRANSLATION: 'view_mindspark_translation',
    VIEW_NEEV_TRANSLATION: 'view_neev_translation',

    // Translation Action Permissions
    ADD_TRANSLATION_KEY: 'add_translation_key',
    ADD_TRANSLATION_VALUE: 'add_translation_value',
    UPLOAD_TRANSLATION: 'upload_translation',
    DOWNLOAD_TRANSLATION: 'download_translation',
    EDIT_TRANSLATION_KEY: 'edit_translation_key',
    EDIT_TRANSLATION_VALUE: 'edit_translation_value',
} as const

/**
 * Type-safe permission name type
 * Allows TypeScript to infer the exact string literal types
 */
export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]
